/**
 * StripeRailChip
 *
 * Compact chip surfacing which Stripe rail an invoice was billed through:
 *   - Stripe Invoices flow (bank ACH / PAD): blue chip with file-invoice icon + in_… ID
 *   - Stripe PaymentIntents flow (cards): purple chip with credit-card icon + pi_… ID
 *   - Neither (still a local-only draft / never pushed): muted em-dash
 *
 * The label is truncated to fit table cells; the full ID is exposed via
 * the `title` attribute so hovering reveals it.
 *
 * Single source of truth for the dual-rail visual treatment — used by the
 * invoice list and detail drawer so both views stay consistent.
 */

import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCreditCard, faFileInvoice, faReceipt } from '@fortawesome/free-solid-svg-icons'
import styles from './StripeRailChip.module.scss'

const RAIL_CONFIG = {
  invoice: { label: 'Invoice', icon: faFileInvoice },
  payment_intent: { label: 'Card', icon: faCreditCard },
}

function deriveRail(invoice) {
  if (invoice?.stripe_payment_intent_id) {
    return { rail: 'payment_intent', id: invoice.stripe_payment_intent_id }
  }
  if (invoice?.stripe_invoice_id) {
    return { rail: 'invoice', id: invoice.stripe_invoice_id }
  }
  return { rail: null, id: null }
}

function truncate(id) {
  if (!id) return ''
  if (id.length <= 14) return id
  return `${id.slice(0, 8)}…${id.slice(-3)}`
}

const StripeRailChip = ({ invoice = null }) => {
  const { rail, id } = deriveRail(invoice)
  if (!rail) {
    return (
      <span data-testid="stripe-rail-chip-empty" className={styles.empty}>
        —
      </span>
    )
  }
  const cfg = RAIL_CONFIG[rail]
  const receiptUrl = invoice?.stripe_receipt_url
  return (
    <span className={styles.wrapper}>
      <span
        data-testid="stripe-rail-chip"
        data-rail={rail}
        title={`${cfg.label} — ${id}`}
        aria-label={`${cfg.label} ${id}`}
        className={`${styles.chip} ${styles[rail]}`}
      >
        <FontAwesomeIcon icon={cfg.icon} className={styles.icon} aria-hidden="true" />
        <span className={styles.id}>{truncate(id)}</span>
      </span>
      {receiptUrl ? (
        <a
          data-testid="stripe-receipt-link"
          href={receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.receiptLink}
          title="View Stripe receipt"
          onClick={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faReceipt} className={styles.receiptIcon} aria-hidden="true" />
          <span>Receipt</span>
        </a>
      ) : null}
    </span>
  )
}

StripeRailChip.propTypes = {
  invoice: PropTypes.shape({
    stripe_invoice_id: PropTypes.string,
    stripe_payment_intent_id: PropTypes.string,
    stripe_receipt_url: PropTypes.string,
  }),
}


export default StripeRailChip
